import { useMemo } from "react";
import type { AdminProviderRow } from "../types/adminProvider";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery } from "./useCachedQuery";

export type AdminProvidersStatus = "loading" | "ready" | "error";

export type UseAdminProvidersResult = {
  status: AdminProvidersStatus;
  providers: ReadonlyArray<AdminProviderRow>;
  error: string | null;
  isFetching: boolean;
  refetch: () => Promise<void>;
};

// IP-222: loads the per-provider catalog breakdown for the admin Integrations
// tab. Read-only GET (no CSRF). Re-fetches whenever `includeInactive` flips,
// since that's a server-side filter rather than a client one. Only the
// default (active-only) view is cached.
export const useAdminProviders = (
  includeInactive: boolean,
): UseAdminProvidersResult => {
  const url = `/api/admin/integration-catalog/providers?includeInactive=${includeInactive}`;

  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<ReadonlyArray<AdminProviderRow>> => {
      const res = await fetch(url, { credentials: "include", signal });
      if (!res.ok) throw new Error(`Failed to load catalog (${res.status})`);
      const body = (await res.json()) as { providers: ReadonlyArray<AdminProviderRow> };
      return body.providers;
    },
    [url],
  );

  const { status, data, error, isFetching, refetch } = useCachedQuery<ReadonlyArray<AdminProviderRow>>({
    cacheResource: includeInactive ? null : ADMIN_QUERIES.providers.resource,
    queryKey: url,
    fetcher,
  });

  return { status, providers: data ?? [], error, isFetching, refetch };
};
